import React, { useEffect } from "react";
import {
	Admin,
	Resource,
	Layout,
	Menu,
	SimpleForm,
	TextInput,
	CustomRoutes,
} from "react-admin";
import { Route, Routes } from "react-router-dom";
import Navigation from "./components/Navigation";
import { AppBar, UserMenu, Logout } from "react-admin";
import dataProvider from "./dataProvider";
import authProvider from "./authProvider";
import {
	UsersList,
	UsersEdit,
	UsersCreate,
	UsersShow,
} from "./components/ra_users";
import {
	ProjectsList,
	ProjectsCreate,
	ProjectShow,
} from "./components/ra_projects";
import { EmailsList, EmailEdit } from "./components/ra_emails";
import CustomLoginPage from "./components/CustomLoginPage";
import { MenuItemLink } from "react-admin";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Work";
import EmailIcon from "@mui/icons-material/Email";
import UserProfileEdit from "./components/ra_userPage";
import WorkIcon from "@mui/icons-material/Work";
import SettingsIcon from "@mui/icons-material/Settings";
import BuildIcon from "@mui/icons-material/Build";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ProfileIcon from "@mui/icons-material/AccountCircle";
import GitHubIcon from "@mui/icons-material/GitHub";
import { useAuthContext } from "./App";
import GithubSettings from "./components/ra_githubSettings";
import SiteContentPage from "./components/ra_siteContent";
import { ServicesList, ServicesEdit, ServicesCreate } from "./components/ra_services";
import { PeopleList, PeopleEdit, PeopleCreate } from "./components/ra_people";
import { CollaborationsList, CollaborationsEdit, CollaborationsCreate } from "./components/ra_collaborations";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import WebIcon from "@mui/icons-material/Web";
import PeopleIcon from "@mui/icons-material/People";
import HandshakeIcon from "@mui/icons-material/Handshake";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Dashboard from "./components/ra_dashboard";

// Menu.Item's `to` is passed straight to a plain react-router <Link> with no
// awareness of <Admin basename="/admin">, unlike Menu.ResourceItem (which
// resolves resource paths through react-admin's own basename-aware helpers).
// These need the /admin prefix spelled out explicitly or they navigate to a
// top-level route that doesn't exist (e.g. "/dashboard" instead of
// "/admin/dashboard").
const AdminMenu = () => (
	<Menu>
		<Menu.Item to="/admin/dashboard" primaryText="Dashboard" leftIcon={<DashboardIcon />} />
		<Menu.ResourceItem name="projects" />
		<Menu.ResourceItem name="services" />
		<Menu.ResourceItem name="people" />
		<Menu.ResourceItem name="collaborations" />
		<Menu.ResourceItem name="emails" />
		<Menu.ResourceItem name="users" />
		<Menu.Item to="/admin/site-content" primaryText="Site Content" leftIcon={<WebIcon />} />
		<Menu.Item to="/admin/settings/github" primaryText="GitHub Settings" leftIcon={<GitHubIcon />} />
	</Menu>
);

// react-admin's own AppBar is position:fixed and pinned to the viewport top,
// so it ignores any marginTop given to the Layout below — it has to be pushed
// down independently, past the site's own fixed nav bar (~80px tall).
const AdminAppBar = (props) => <AppBar {...props} sx={{ top: '90px' }} />;

const CustomLayout = (props) => (
	<Layout {...props} menu={AdminMenu} appBar={AdminAppBar} sx={{ marginTop: '150px' }} />
);

const AdminApp = () => {
	return (
		<Admin
			layout={CustomLayout} // Apply the custom layout
			basename="/admin"
			dataProvider={dataProvider}
			authProvider={authProvider}
			loginPage={CustomLoginPage}
			dashboard={Dashboard}
		>
			{/* Define your resources here */}
			{(permissions) => [
				permissions >= 99 ? (
					<Resource
						name="users"
						list={UsersList}
						edit={UsersEdit}
						create={UsersCreate}
						show={UsersShow}
						icon={PersonIcon}
					/>
				) : null,
				<Resource
					name="projects"
					list={ProjectsList}
					create={permissions >= 98 ? ProjectsCreate : null}
					show={ProjectShow}
					icon={GroupIcon}
				/>,
				permissions >= 98 ? (
					<Resource
						name="emails"
						list={EmailsList}
						edit={EmailEdit}
						icon={EmailIcon}
					/>
				) : null,
				permissions >= 98 ? (
					<Resource
						name="services"
						list={ServicesList}
						edit={ServicesEdit}
						create={ServicesCreate}
						icon={DesignServicesIcon}
					/>
				) : null,
				permissions >= 98 ? (
					<Resource
						name="people"
						list={PeopleList}
						edit={PeopleEdit}
						create={PeopleCreate}
						icon={PeopleIcon}
					/>
				) : null,
				permissions >= 98 ? (
					<Resource
						name="collaborations"
						list={CollaborationsList}
						edit={CollaborationsEdit}
						create={CollaborationsCreate}
						icon={HandshakeIcon}
					/>
				) : null,
			]}
			{/* Custom Routes */}
			<CustomRoutes>
				<Route path="/dashboard" element={<Dashboard />} />
				<Route path="/profile" element={<UserProfileEdit />} />
				<Route path="/login" element={<CustomLoginPage />} />
				{/* GithubSettings checks role >= 99 internally */}
				<Route path="/settings/github" element={<GithubSettings />} />
				<Route path="/site-content" element={<SiteContentPage />} />
			</CustomRoutes>
		</Admin>
	);
};

export default AdminApp;
